import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from './firebase';

const getUserPath = (userId) => `users/${userId}`;

export const addDocument = async (userId, collectionName, data) => {
  try {
    const userCollectionRef = collection(db, getUserPath(userId), collectionName);
    const docRef = await addDoc(userCollectionRef, {
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    // También actualizar lastLogin en el documento del usuario
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        lastLogin: new Date().toISOString()
      });
    } catch (err) {
      console.log('Could not update lastLogin:', err);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding document:', error);
    throw error;
  }
};

export const updateDocument = async (userId, collectionName, docId, data) => {
  try {
    const docRef = doc(db, getUserPath(userId), collectionName, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
    
    // También actualizar lastLogin en el documento del usuario
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        lastLogin: new Date().toISOString()
      });
    } catch (err) {
      console.log('Could not update lastLogin:', err);
    }
    
    return true;
  } catch (error) {
    console.error('Error updating document:', error);
    throw error;
  }
};

export const updateUserDocument = async (userId, data) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating user document:', error);
    throw error;
  }
};

export const getNextSaleNumber = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      throw new Error('Usuario no encontrado');
    }
    
    const userData = userDoc.data();
    const currentSaleNumber = userData.lastSaleNumber || 0;
    const nextSaleNumber = currentSaleNumber + 1;
    
    // Actualizar el contador en el documento del usuario
    await updateDoc(userDocRef, {
      lastSaleNumber: nextSaleNumber,
      updatedAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    });
    
    return nextSaleNumber;
  } catch (error) {
    console.error('Error getting next sale number:', error);
    throw error;
  }
};

export const deleteDocument = async (userId, collectionName, docId) => {
  try {
    const docRef = doc(db, getUserPath(userId), collectionName, docId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

export const getDocument = async (userId, collectionName, docId) => {
  try {
    const docRef = doc(db, getUserPath(userId), collectionName, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting document:', error);
    throw error;
  }
};

export const getDocuments = async (userId, collectionName, filters = [], orderByField = 'createdAt', orderDirection = 'desc', limitCount = null) => {
  try {
    let q = collection(db, getUserPath(userId), collectionName);
    
    if (filters.length > 0) {
      const conditions = filters.map(filter => where(filter.field, filter.operator, filter.value));
      q = query(q, ...conditions);
      // No aplicar orderBy cuando hay filtros para evitar requerimiento de índices compuestos
      if (limitCount) {
        q = query(q, limit(limitCount));
      }
    } else {
      // Solo aplicar orderBy cuando no hay filtros
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }
      if (limitCount) {
        q = query(q, limit(limitCount));
      }
    }
    
    const querySnapshot = await getDocs(q);
    const documents = [];
    
    querySnapshot.forEach((doc) => {
      documents.push({ id: doc.id, ...doc.data() });
    });
    
    // Si hay filtros y se especificó ordenamiento, ordenar en memoria
    if (filters.length > 0 && orderByField) {
      documents.sort((a, b) => {
        const aValue = a[orderByField];
        const bValue = b[orderByField];
        if (orderDirection === 'desc') {
          return bValue > aValue ? 1 : -1;
        }
        return aValue > bValue ? 1 : -1;
      });
    }
    
    return documents;
  } catch (error) {
    console.error('Error getting documents:', error);
    throw error;
  }
};

export const searchDocuments = async (userId, collectionName, searchField, searchValue) => {
  try {
    const q = query(
      collection(db, getUserPath(userId), collectionName),
      where(searchField, '>=', searchValue),
      where(searchField, '<=', searchValue + '\uf8ff')
    );
    
    const querySnapshot = await getDocs(q);
    const documents = [];
    
    querySnapshot.forEach((doc) => {
      documents.push({ id: doc.id, ...doc.data() });
    });
    
    return documents;
  } catch (error) {
    console.error('Error searching documents:', error);
    throw error;
  }
};